import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import {
  User, Phone, MapPin, CalendarCheck, CalendarX,
  Star, CheckCircle2, X, ChevronRight,
} from "lucide-react";
import { CreateNotes, UpdateNotes } from "adminApi/RoomStatusApi/RoomStatusApi";

// ── Info row ──────────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="d-flex align-items-start gap-3 py-2 border-bottom">
    <Icon size={14} className="text-muted flex-shrink-0 mt-1" />
    <div style={{ minWidth: 0 }}>
      <div className="text-muted mb-1" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div className="fw-medium text-body" style={{ fontSize: "0.85rem", wordBreak: "break-word" }}>
        {value || "—"}
      </div>
    </div>
  </div>
);

// ── Section label ─────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <div className="text-muted fw-bold text-uppercase mt-4 mb-2" style={{ fontSize: "0.62rem", letterSpacing: "0.1em" }}>
    {children}
  </div>
);

// ── Main modal ────────────────────────────────────────────────────────────────
const RoomDetailModal = ({ show, handleClose, room }) => {
  const [noteText, setNoteText] = useState("");
  const [problems, setProblems] = useState([]);
  const [solved, setSolved] = useState([]);

  useEffect(() => {
    if (!room?.notes) { setProblems([]); setSolved([]); return; }
    const notes = typeof room.notes === 'string' ? JSON.parse(room.notes) : room.notes;
    setProblems(notes?.filter(n => n.note_status === "PROBLEM")?.map(n => ({ id: n.note_id, text: n.note_text })) || []);
    setSolved(notes?.filter(n => n.note_status === "SOLVED")?.map(n => ({ id: n.note_id, text: n.note_text })) || []);
  }, [room]);

  if (!room) return null;

  const rv = (key) => {
    const map = { check_out: "checkout_date", customer_address: "address", adults: "no_of_people", rating: "number_of_star", rent_day: "room_rate" };
    return room[key] ?? room[map[key]] ?? "—";
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await CreateNotes({ room_assign_id: room.room_assign_id, note_text: noteText, note_status: "PROBLEM" });
      setProblems(p => [...p, { id: res.insertId, text: noteText }]);
      setNoteText("");
    } catch (e) { console.error(e); }
  };

  const solveSingle = async (p) => {
    try {
      await UpdateNotes(p.id, { note_status: "SOLVED" });
      setSolved(s => [...s, p]);
      setProblems(ps => ps.filter(x => x.id !== p.id));
    } catch (e) { console.error(e); }
  };

  const solveAll = async () => {
    try {
      await Promise.all(problems.map(p => UpdateNotes(p.id, { note_status: "SOLVED" })));
      setSolved(s => [...s, ...problems]);
      setProblems([]);
    } catch (e) { console.error(e); }
  };

  const rootContainer = document.getElementById("root-container");
  const stars = parseInt(rv("rating")) || 0;

  return (
    <Modal
      container={rootContainer || null}
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      scrollable
    >
      <Modal.Body className="p-0 bg-body overflow-hidden" style={{ borderRadius: 12 }}>

        {/* ── HEADER ─────────────────────────────────────────────────────── */}
        <div className="d-flex align-items-start justify-content-between px-3 px-sm-4 py-3 border-bottom">
          <div>
            <div className="fw-bold text-body" style={{ fontSize: "clamp(1.1rem, 4vw, 1.4rem)", lineHeight: 1 }}>
              Room {room.room_no}
            </div>
            <div className="text-muted mt-1 d-flex align-items-center flex-wrap gap-2" style={{ fontSize: "0.78rem" }}>
              <span>{rv("room_type") || "—"}</span>
              <span className="badge bg-body-secondary text-body-secondary rounded-pill fw-semibold" style={{ fontSize: "0.62rem" }}>
                {room.booking_status || "Available"}
              </span>
            </div>
          </div>
          <button onClick={handleClose} className="btn btn-sm btn-link text-muted p-1 ms-2 flex-shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* ── BODY ───────────────────────────────────────────────────────── */}
        <div className="px-3 px-sm-4 pb-4" style={{ maxHeight: "65vh", overflowY: "auto" }}>

          {/* Financial chips */}
          <SectionLabel>Financials</SectionLabel>
          <div className="row g-2 mb-2">
            {[
              { label: "Paid", value: rv("paid_amount") },
              { label: "Due", value: rv("due_amount") },
              { label: "Rate/Night", value: rv("rent_day") },
            ].map(({ label, value }) => (
              <div key={label} className="col-4">
                <div className="p-2 p-sm-3 rounded-3 bg-body-secondary h-100">
                  <div className="text-muted mb-1" style={{ fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                  <div className="fw-bold text-body" style={{ fontSize: "clamp(0.8rem, 2.5vw, 0.95rem)" }}>{value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Guest + Booking — stacks on mobile, side by side on md+ */}
          <div className="row g-0">
            <div className="col-12 col-md-6 pe-md-3">
              <SectionLabel>Guest</SectionLabel>
              <InfoRow icon={User} label="Name" value={rv("customer_name")} />
              <InfoRow icon={Phone} label="Phone" value={rv("customer_phone")} />
              <InfoRow icon={MapPin} label="Address" value={rv("customer_address")} />
            </div>
            <div className="col-12 col-md-6 ps-md-3">
              <SectionLabel>Booking</SectionLabel>
              <InfoRow icon={CalendarCheck} label="Check-In" value={rv("checkin_date")} />
              <InfoRow icon={CalendarX} label="Check-Out" value={rv("check_out")} />
              <InfoRow icon={User} label="Adults" value={rv("adults")} />
            </div>
          </div>

          {/* Rating */}
          <SectionLabel>Rating</SectionLabel>
          <div className="d-flex gap-1 mb-2">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} size={15} fill={i <= stars ? "#fbbf24" : "none"} color={i <= stars ? "#fbbf24" : "var(--bs-border-color)"} />
            ))}
          </div>

          <hr className="my-3" />

          {/* Notes */}
          <SectionLabel>Notes & Issues</SectionLabel>

          <div className="d-flex gap-2 mb-3">
            <input
              className="form-control form-control-sm"
              value={noteText}
              placeholder="Write a note..."
              onChange={e => setNoteText(e.target.value)}
              onKeyPress={e => e.key === "Enter" && addNote()}
            />
            <button
              className="btn btn-sm btn-dark px-3 flex-shrink-0"
              onClick={addNote}
              disabled={!noteText.trim()}
            >
              Add
            </button>
          </div>

          {/* Problems + Solved — full width on xs, half on sm+ */}
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-muted fw-semibold" style={{ fontSize: "0.72rem" }}>Problems</span>
                <span className="badge bg-body-secondary text-muted rounded-pill" style={{ fontSize: "0.62rem" }}>{problems.length}</span>
              </div>
              <div style={{ maxHeight: 150, overflowY: "auto" }}>
                {problems.length === 0
                  ? <p className="text-muted fst-italic small mb-0">None reported</p>
                  : problems.map(p => (
                    <label key={p.id} className="d-flex align-items-center gap-2 mb-1 p-2 rounded bg-body-secondary border" style={{ fontSize: "0.78rem", cursor: "pointer" }}>
                      <input type="checkbox" className="form-check-input mt-0 flex-shrink-0" onChange={() => solveSingle(p)} />
                      <span className="text-body">{p.text}</span>
                    </label>
                  ))
                }
              </div>
              {problems.length > 0 && (
                <button className="btn btn-sm btn-outline-secondary w-100 mt-2" style={{ fontSize: "0.72rem" }} onClick={solveAll}>
                  Mark All Solved
                </button>
              )}
            </div>

            <div className="col-12 col-sm-6">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-muted fw-semibold" style={{ fontSize: "0.72rem" }}>Solved</span>
                <span className="badge bg-body-secondary text-muted rounded-pill" style={{ fontSize: "0.62rem" }}>{solved.length}</span>
              </div>
              <div style={{ maxHeight: 150, overflowY: "auto" }}>
                {solved.length === 0
                  ? <p className="text-muted fst-italic small mb-0">None yet</p>
                  : solved.map(s => (
                    <div key={s.id} className="d-flex align-items-center gap-2 mb-1 p-2 rounded bg-body-secondary border" style={{ fontSize: "0.78rem" }}>
                      <CheckCircle2 size={13} className="text-muted flex-shrink-0" />
                      <span className="text-muted text-decoration-line-through">{s.text}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        </div>

        {/* ── FOOTER ─────────────────────────────────────────────────────── */}
        <div className="d-flex align-items-center justify-content-end gap-2 px-3 px-sm-4 py-3 border-top flex-wrap">
          <button className="btn btn-sm btn-outline-secondary px-4" onClick={handleClose}>
            Close
          </button>
          <NavLink
            to="/admin/room-booking-list/"
            className="btn btn-sm btn-dark px-4 d-flex align-items-center gap-1 fw-semibold"
          >
            Book Now <ChevronRight size={14} />
          </NavLink>
        </div>

      </Modal.Body>
    </Modal>
  );
};

RoomDetailModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  room: PropTypes.object,
};

export default RoomDetailModal;
